/* =============================================
   Curator's Pick — Revolving Slider
   File: assets/curators-pick.js

   Card positions (relative to center):
     -2  : far left  (hidden)
     -1  : left      (behind center)
      0  : center    (active, front)
     +1  : right     (behind center)
     +2  : far right (hidden)

   Navigation:
     - Prev/Next buttons
     - Click a side card to focus it
     - Drag (desktop) / Swipe (mobile) via GSAP Draggable
   ============================================= */

(function () {

  const POSITIONS = {
      '-2': { xPercent: -80, scale: 0.65, opacity: 0,   zIndex: 1 },
      '-1': { xPercent:  -48, scale: 0.80, opacity: 0.7, zIndex: 2 },
      '0': { xPercent:    0, scale: 1.00, opacity: 1,   zIndex: 3 },
      '1': { xPercent:   48, scale: 0.80, opacity: 0.7, zIndex: 2 },
      '2': { xPercent:  80, scale: 0.65, opacity: 0,   zIndex: 1 },
  };

  /* Minimum drag distance (px) to trigger a slide change */
  const DRAG_THRESHOLD = 60;

  function clampOffset(offset) {
    return Math.max(-2, Math.min(2, offset));
  }

  function getPositionConfig(offset) {
    return POSITIONS[String(clampOffset(offset))];
  }

  function initCuratorsPick(section) {
    const track     = section.querySelector('.cp-track');
    const cards     = Array.from(section.querySelectorAll('.cp-card'));
    const infoItems = Array.from(section.querySelectorAll('.cp-info__item'));
    const prevBtn   = section.querySelector('.cp-btn--prev');
    const nextBtn   = section.querySelector('.cp-btn--next');

    if (!track || !cards.length) return;

    const total    = cards.length;
    let activeIndex  = 0;
    let isAnimating  = false;
    const duration   = (parseInt(section.dataset.duration, 10) || 600) / 1000;

    /* ── Track height ── */
    function setTrackHeight() {
      const cardWidth = cards[0].offsetWidth;
      track.style.height = (cardWidth * (4 / 3)) + 'px';
    }

    /* ── Position a single card ── */
    function positionCard(card, offset, animate) {
      const cfg = getPositionConfig(offset);

      card.classList.remove('is-active', 'is-prev', 'is-next', 'is-hidden');
      if      (offset ===  0) card.classList.add('is-active');
      else if (offset === -1) card.classList.add('is-prev');
      else if (offset ===  1) card.classList.add('is-next');
      else                    card.classList.add('is-hidden');

      if (animate) {
        gsap.to(card, {
          xPercent:  cfg.xPercent,
          scale:     cfg.scale,
          opacity:   cfg.opacity,
          zIndex:    cfg.zIndex,
          duration:  duration,
          ease:      'power2.inOut',
          overwrite: 'auto',
        });
      } else {
        gsap.set(card, {
          xPercent: cfg.xPercent,
          scale:    cfg.scale,
          opacity:  cfg.opacity,
          zIndex:   cfg.zIndex,
        });
      }
    }

    /* ── Offset of a card relative to activeIndex (with wrap) ── */
    function getOffset(cardIndex) {
      let offset = cardIndex - activeIndex;
      if (offset >  Math.floor(total / 2)) offset -= total;
      if (offset < -Math.floor(total / 2)) offset += total;
      return offset;
    }

    /* ── Layout all cards ── */
    function layoutCards(animate) {
      cards.forEach((card, i) => positionCard(card, getOffset(i), animate));
    }

    /* ── Info panel ── */
    function updateInfo(index) {
      infoItems.forEach((item, i) => {
        const active = i === index;
        item.classList.toggle('cp-info__item--active', active);
        item.style.position     = active ? 'relative'  : 'absolute';
        item.style.pointerEvents = active ? 'auto'     : 'none';
      });
    }

    /* ── Core navigate function ── */
    function goTo(newIndex) {
      if (isAnimating) return;
      isAnimating = true;

      activeIndex = ((newIndex % total) + total) % total;
      layoutCards(true);
      updateInfo(activeIndex);

      setTimeout(() => { isAnimating = false; }, duration * 1000 + 50);
    }

    /* ── Buttons ── */
    if (prevBtn) prevBtn.addEventListener('click', () => goTo(activeIndex - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(activeIndex + 1));

    /* ── Side card click ── */
    cards.forEach((card, i) => {
      card.addEventListener('click', () => {
        if (i !== activeIndex) goTo(i);
      });
    });

    /* ── GSAP Draggable — drag & swipe ──
       We create an invisible proxy element that Draggable
       controls. On drag end we read how far it moved and
       decide direction — then snap it back to 0 so it's
       always ready for the next drag.                      */
    const proxy = document.createElement('div');
    proxy.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(proxy);

    Draggable.create(proxy, {
      type: 'x',
      trigger: track,       /* listen for drag events on the track */
      edgeResistance: 1,    /* no rubberbanding */
      onDragEnd: function () {
        const dragX    = this.endX - this.startX;
        const velocity = this.velocityX || 0;

        /* Trigger on either sufficient distance OR fast flick */
        const byDistance = Math.abs(dragX)    >= DRAG_THRESHOLD;
        const byVelocity = Math.abs(velocity) >= 500;

        if (byDistance || byVelocity) {
          /* Dragging left → next, dragging right → prev */
          if (dragX < 0) {
            goTo(activeIndex + 1);
          } else {
            goTo(activeIndex - 1);
          }
        }

        /* Reset proxy position so next drag starts clean */
        gsap.set(proxy, { x: 0 });
      },
    });

    /* ── Init ── */
    setTrackHeight();
    layoutCards(false);
    updateInfo(0);

    window.addEventListener('resize', () => {
      setTrackHeight();
      layoutCards(false);
    });
  }

  /* ── Wait for GSAP + Draggable then init ── */
  function init() {
    if (typeof gsap === 'undefined' || typeof Draggable === 'undefined') {
      setTimeout(init, 50);
      return;
    }

    document.querySelectorAll('.curators-pick').forEach(initCuratorsPick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
